import { takeEvery, fork, put, all, call } from 'redux-saga/effects';
import {
  addCustomer,
  addCustomerSuccess,
  addCustomerFailure,
  updateCustomerActive,
  updateCustomerActiveSuccess,
  updateCustomerActiveFailure,
  deleteCustomer,
  deleteCustomerSuccess,
  deleteCustomerFailure,
  getCustomerOrgAccounts,
  getCustomerOrgAccountsSuccess,
  getCustomerOrgAccountsFailure,
} from './customerSlice';
import {
  addCustomer as addCustomerApi,
  updateCustomerActive as updateCustomerActiveApi,
  deleteCustomer as deleteCustomerApi,
  getCustomerOrgAccounts as getCustomerOrgAccountsApi,
} from '../../helpers/auth_aws_helper';

// Helper function to handle API errors
const handleApiError = (error, operation) => {
  console.error(`${operation} Error:`, error);
  
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const data = error.response.data;
    
    let errorMessage = `Error Code: ${status}`;
    
    if (data) {
      // Check for error key first (API Gateway format)
      if (data.error && typeof data.error === "string") {
        errorMessage = data.error;
      } else if (data.message && typeof data.message === "string") {
        errorMessage = data.message;
      } else if (typeof data === "string") {
        errorMessage = data;
      } else if (typeof data === "object") {
        // Try to find any string property that might contain the error message
        for (const [key, value] of Object.entries(data)) {
          if (typeof value === "string" && value.length > 0) {
            errorMessage = value;
            break;
          }
        }
      }
    }
    
    return errorMessage;
  } else if (error.request) {
    // Request was made but no response received
    return "No response received from server. Please check your connection.";
  } else {
    // Something else happened
    return error.message || "An unexpected error occurred";
  }
};

// Saga for adding customer
function* addCustomerSaga({ payload }) {
  try {
    console.log("Saga: Calling addCustomer with payload =", payload);
    const response = yield call(addCustomerApi, payload);
    console.log("Saga: Add Customer Response =", response);

    if (response.status === 200 || response.status === 201) {
      // Handle success response
      const successData = { 
        data: response.data 
      };
      yield put(addCustomerSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(addCustomerFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Add Customer");
    yield put(addCustomerFailure(errorMessage));
  }
}

// Saga for getting customer org accounts
function* getCustomerOrgAccountsSaga() {
  try {
    console.log("Saga: Calling getCustomerOrgAccounts");
    const response = yield call(getCustomerOrgAccountsApi);
    console.log("Saga: Get Customer Org Accounts Response =", response);

    if (response.status === 200 || response.status === 201) {
      // NOTE: aws_api_helper.get() returns { ...response.data, status }
      // (not an Axios-style { data, status } shape). Normalize here.
      const normalizedData =
        response && typeof response === "object"
          ? (Object.prototype.hasOwnProperty.call(response, "data")
              ? response.data
              : (() => {
                  // Strip status and treat the remaining keys as the response body
                  // (account_number keyed object).
                  // eslint-disable-next-line no-unused-vars
                  const { status, ...rest } = response;
                  return rest;
                })())
          : response;

      const successData = { data: normalizedData };
      yield put(getCustomerOrgAccountsSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getCustomerOrgAccountsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Get Customer Org Accounts");
    yield put(getCustomerOrgAccountsFailure(errorMessage));
  }
}

// Saga for updating customer active status
function* updateCustomerActiveSaga({ payload }) {
  try {
    console.log("Saga: Calling updateCustomerActive with payload =", payload);
    const response = yield call(updateCustomerActiveApi, payload);
    console.log("Saga: Update Customer Active Response =", response);

    if (response.status === 200 || response.status === 201 || response.status === 204) {
      let responseData;
      if (typeof response.data === "string") {
        responseData = response.data;
      } else if (response.data && typeof response.data === "object") {
        responseData = response.data.data || response.data.message || response.data;
      } else {
        responseData = response.data || "Customer status updated successfully";
      }

      yield put(
        updateCustomerActiveSuccess({
          data: responseData,
          message: responseData,
          active: payload?.active,
          orgcode: payload?.orgcode,
        })
      );
    } else {
      let errorMessage = `Unexpected response: ${response.status}`;

      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }

      yield put(updateCustomerActiveFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Customer Active Status");
    yield put(updateCustomerActiveFailure(errorMessage));
  }
}

// Saga for deleting customer
function* deleteCustomerSaga({ payload }) {
  try {
    console.log("Saga: Calling deleteCustomer with orgCode =", payload);
    const response = yield call(deleteCustomerApi, payload);
    console.log("Saga: Delete Customer Response =", response);

    if (response.status === 200 || response.status === 204) {
      // Handle success response
      // Make message same as data - use the actual response data
      let responseData;
      if (typeof response.data === 'string') {
        responseData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object, check for data property first
        responseData = response.data.data || response.data.message || JSON.stringify(response.data);
      } else {
        responseData = response.data || "Customer deleted successfully";
      }
      
      const successData = { 
        data: responseData,
        message: responseData  // Make message same as data
      };
      yield put(deleteCustomerSuccess(successData));
    } else {
      // Handle non-200 status codes - pass the actual backend error data
      let errorData = response.data;
      
      // If response.data is an object with data property, use that
      if (response.data && typeof response.data === "object" && response.data.data) {
        errorData = response.data.data;
      } else if (response.data && typeof response.data === "string") {
        errorData = response.data;
      } else if (!errorData) {
        errorData = `Unexpected response: ${response.status}`;
      }
      
      yield put(deleteCustomerFailure(errorData));
    }
  } catch (error) {
    // Pass the actual error response data instead of processed message
    let errorData;
    if (error.response && error.response.data) {
      // Use the actual backend error data
      if (typeof error.response.data === "string") {
        errorData = error.response.data;
      } else if (error.response.data.data) {
        errorData = error.response.data.data;
      } else if (error.response.data.error) {
        errorData = error.response.data.error;
      } else if (error.response.data.message) {
        errorData = error.response.data.message;
      } else {
        errorData = JSON.stringify(error.response.data);
      }
    } else {
      // Fallback to processed error message if no response data
      errorData = handleApiError(error, "Delete Customer");
    }
    yield put(deleteCustomerFailure(errorData));
  }
}

// Watcher saga
function* watchCustomer() {
  yield takeEvery(addCustomer.type, addCustomerSaga);
  yield takeEvery(updateCustomerActive.type, updateCustomerActiveSaga);
  yield takeEvery(deleteCustomer.type, deleteCustomerSaga);
  yield takeEvery(getCustomerOrgAccounts.type, getCustomerOrgAccountsSaga);
}

// Root saga
function* customerSaga() {
  yield all([fork(watchCustomer)]);
}

export default customerSaga;
